import { formatDateWithTimeZone } from "@cp/toolkit";
import type { DraftMessageFragment } from "@qw/graphql";
import {
  Alert,
  AlertDescription,
  Button,
  ButtonGroup,
  Group,
  Icon,
  Spinner,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@qw/ui";
import type { FC } from "react";
import { useDraft } from "@/providers/draft-provider";

export const DraftScheduledSendAlert = () => {
  const { draft } = useDraft();

  switch (draft?.scheduledSend?.status) {
    case "pending":
    case "queued":
      return <DraftScheduledSendPendingAlert scheduledSend={draft.scheduledSend} />;
    case "failed":
      return <DraftScheduledSendFailedAlert scheduledSend={draft.scheduledSend} />;
    default:
      return null;
  }
};

export const DraftScheduledSendPendingAlert: FC<{
  scheduledSend: NonNullable<DraftMessageFragment["scheduledSend"]>;
}> = ({ scheduledSend }) => (
  <div className="flex-0 px-6 pt-4 pb-4">
    <Alert icon="schedule_send" variant="primary" className="items-center pl-4 pr-3.5 py-3">
      <Group direction="row" className="items-center justify-between">
        <AlertDescription className="flex-row items-start">
          <p>
            Send scheduled for{" "}
            <strong>{formatDateWithTimeZone(new Date(scheduledSend.sendAt), "EEE, LLL d, yyyy 'at' h:mm a")}</strong>.
          </p>
        </AlertDescription>

        <ButtonGroup className="justify-end gap-2">
          <RescheduleSendButton />
          <UnscheduleSendButton />
        </ButtonGroup>
      </Group>
    </Alert>
  </div>
);

const RescheduleSendButton: FC = () => {
  const { handleRescheduleSendDraft, isRescheduling, isUnscheduling } = useDraft();

  return (
    <Button variant="outline" size="sm" onClick={handleRescheduleSendDraft} disabled={isRescheduling || isUnscheduling}>
      {isRescheduling ? (
        <>
          <Spinner className="size-3" />
          Rescheduling&hellip;
        </>
      ) : (
        <>Reschedule</>
      )}
    </Button>
  );
};

const UnscheduleSendButton: FC = () => {
  const { handleUnscheduleSendDraft, isUnscheduling, isRescheduling } = useDraft();

  return (
    <Button
      theme="destructive"
      size="sm"
      onClick={handleUnscheduleSendDraft}
      disabled={isUnscheduling || isRescheduling}
    >
      {isUnscheduling ? (
        <>
          <Spinner className="size-3" />
          Cancelling&hellip;
        </>
      ) : (
        <>Cancel</>
      )}
    </Button>
  );
};

export const DraftScheduledSendFailedAlert: FC<{
  scheduledSend: NonNullable<DraftMessageFragment["scheduledSend"]>;
}> = ({ scheduledSend }) => (
  <div className="flex-0 px-6 pt-4">
    <Alert icon="error" variant="destructive" className="items-center pl-4 pr-3.5 py-3">
      <Group direction="row" className="items-center justify-between">
        <AlertDescription className="flex-row items-start">
          <p>
            Failed to send at scheduled time of{" "}
            <strong>{formatDateWithTimeZone(new Date(scheduledSend.sendAt), "EEE, LLL d, yyyy 'at' h:mm a")}</strong>.{" "}
          </p>
        </AlertDescription>
        <ButtonGroup className="justify-end gap-2">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="ghost" size="sm" display="icon" className="-m-2">
                <Icon icon="info" />
              </Button>
            </TooltipTrigger>
            <TooltipContent align="end" className="max-w-3xs">
              <p>{scheduledSend.failedReason ?? "No reason provided"}</p>
            </TooltipContent>
          </Tooltip>
        </ButtonGroup>
      </Group>
    </Alert>
  </div>
);
